import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Box, Typography, CircularProgress, Alert, Button, Card, CardContent } from '@mui/material';
import { apiService } from '../services/apiService';

const Payment: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const planType = searchParams.get('plan'); // STANDARD or PROFESSIONAL
  const organizationId = searchParams.get('organizationId') || searchParams.get('orgId'); // Support both param names
  const billingCycle = searchParams.get('billingCycle') || 'yearly'; // MONTHLY or YEARLY, default to yearly

  useEffect(() => {
    // If plan type and organization ID are present, create checkout session immediately
    if (planType && (planType === 'STANDARD' || planType === 'PROFESSIONAL')) {
      handleCreateCheckoutSession();
    } else {
      setError('Invalid plan type. Please select a plan from the pricing page.');
    }
  }, [planType, organizationId, billingCycle]);

  const handleCreateCheckoutSession = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.createCheckoutSession({
        planType: planType!,
        organizationId: organizationId ? parseInt(organizationId) : undefined,
        billingCycle: billingCycle.toUpperCase(),
        returnBaseUrl: window.location.origin // So Stripe redirects back to crudexpert.com (or current host)
      });

      if (response.success && response.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = response.checkoutUrl;
      } else {
        setError(response.message || 'Failed to create checkout session');
        setLoading(false);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ py: 8, textAlign: 'center' }}>
        {loading && (
          <Card>
            <CardContent>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Redirecting to secure payment...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait while we redirect you to Stripe Checkout
              </Typography>
            </CardContent>
          </Card>
        )}

        {error && (
          <Card>
            <CardContent>
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
              <Button
                variant="contained"
                onClick={() => navigate('/pricing')}
                sx={{ mr: 2 }}
              >
                Back to Pricing
              </Button>
              <Button
                variant="outlined"
                onClick={handleCreateCheckoutSession}
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>
    </Container>
  );
};

export default Payment;

